import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

interface Week {
  week: number;
  title: string;
  babySize: string;
  babyWeight: string;
  milestone: string;
  symptoms: string[];
  tips: string[];
  tests: string[];
}

const pregnancyData: Week[] = [
  {
    week: 4,
    title: 'Early Pregnancy',
    babySize: 'Poppy seed',
    babyWeight: '<1g',
    milestone: 'Embryo implants in uterus',
    symptoms: ['Missed period', 'Mild cramping', 'Breast tenderness'],
    tips: ['Take prenatal vitamins', 'Schedule first doctor visit', 'Avoid alcohol and smoking'],
    tests: ['Home pregnancy test', 'Blood test (hCG)']
  },
  {
    week: 8,
    title: 'First Trimester',
    babySize: 'Raspberry',
    babyWeight: '1g',
    milestone: 'Heart begins to beat',
    symptoms: ['Morning sickness', 'Fatigue', 'Food aversions'],
    tips: ['Eat small frequent meals', 'Stay hydrated', 'Get plenty of rest'],
    tests: ['First ultrasound', 'Dating scan']
  },
  {
    week: 12,
    title: 'End of First Trimester',
    babySize: 'Lime',
    babyWeight: '14g',
    milestone: 'All organs formed',
    symptoms: ['Reduced nausea', 'Increased energy', 'Visible baby bump'],
    tips: ['Start prenatal exercises', 'Maintain healthy diet', 'Stay active'],
    tests: ['NT scan', 'Blood tests', 'Genetic screening']
  },
  {
    week: 16,
    title: 'Second Trimester Begins',
    babySize: 'Avocado',
    babyWeight: '100g',
    milestone: 'Baby can hear sounds',
    symptoms: ['Glowing skin', 'Growing belly', 'Back pain'],
    tips: ['Talk to your baby', 'Start shopping for maternity clothes', 'Practice good posture'],
    tests: ['Quad screen', 'Amniocentesis (if needed)']
  },
  {
    week: 20,
    title: 'Halfway There!',
    babySize: 'Banana',
    babyWeight: '300g',
    milestone: 'Gender can be determined',
    symptoms: ['Feeling baby movements', 'Leg cramps', 'Heartburn'],
    tips: ['Stay hydrated', 'Elevate legs when resting', 'Eat smaller meals'],
    tests: ['Anatomy scan', 'Gender reveal ultrasound']
  },
  {
    week: 24,
    title: 'Viability Milestone',
    babySize: 'Corn',
    babyWeight: '600g',
    milestone: 'Baby can survive outside womb with medical help',
    symptoms: ['Increased appetite', 'Swollen feet', 'Braxton Hicks contractions'],
    tips: ['Monitor baby movements', 'Wear comfortable shoes', 'Practice relaxation techniques'],
    tests: ['Glucose screening', 'Blood pressure check']
  },
  {
    week: 28,
    title: 'Third Trimester Begins',
    babySize: 'Eggplant',
    babyWeight: '1kg',
    milestone: 'Baby opens eyes',
    symptoms: ['Shortness of breath', 'Frequent urination', 'Difficulty sleeping'],
    tips: ['Sleep on left side', 'Use pregnancy pillow', 'Start childbirth classes'],
    tests: ['Glucose tolerance test', 'Rhesus antibody screening']
  },
  {
    week: 32,
    title: 'Rapid Growth Phase',
    babySize: 'Squash',
    babyWeight: '1.7kg',
    milestone: 'Baby practices breathing',
    symptoms: ['Pelvic pressure', 'Leaking breasts', 'Nesting instinct'],
    tips: ['Prepare hospital bag', 'Finalize birth plan', 'Rest when possible'],
    tests: ['Ultrasound for baby position', 'Group B strep test']
  },
  {
    week: 36,
    title: 'Full Term Approaching',
    babySize: 'Romaine lettuce',
    babyWeight: '2.6kg',
    milestone: 'Baby drops into pelvis',
    symptoms: ['Increased Braxton Hicks', 'Pelvic pain', 'Frequent bathroom trips'],
    tips: ['Monitor contractions', 'Practice breathing exercises', 'Stay close to hospital'],
    tests: ['Weekly checkups begin', 'Cervical exam', 'Non-stress test']
  },
  {
    week: 40,
    title: 'Due Date!',
    babySize: 'Watermelon',
    babyWeight: '3.4kg',
    milestone: 'Baby ready to be born',
    symptoms: ['Loss of mucus plug', 'Water breaking', 'Regular contractions'],
    tips: ['Watch for labor signs', 'Stay calm and relaxed', 'Call doctor when contractions are regular'],
    tests: ['Daily monitoring', 'Biophysical profile', 'Induction discussion if overdue']
  }
];

const hospitalSelectionTimeline = [
  {
    week: '8-12',
    task: 'Research Phase',
    description: 'Start researching hospitals and reading reviews',
    icon: 'ri-search-line',
    color: 'bg-blue-100 text-blue-600'
  },
  {
    week: '12-16',
    task: 'Hospital Visits',
    description: 'Visit 3-5 shortlisted hospitals, tour facilities',
    icon: 'ri-hospital-line',
    color: 'bg-purple-100 text-purple-600'
  },
  {
    week: '16-20',
    task: 'Compare Options',
    description: 'Compare costs, services, NICU availability',
    icon: 'ri-file-list-line',
    color: 'bg-amber-100 text-amber-600'
  },
  {
    week: '20-24',
    task: 'Make Decision',
    description: 'Choose hospital and register for delivery',
    icon: 'ri-checkbox-circle-line',
    color: 'bg-emerald-100 text-emerald-600'
  },
  {
    week: '24-28',
    task: 'Pre-register',
    description: 'Complete hospital paperwork and insurance',
    icon: 'ri-file-text-line',
    color: 'bg-teal-100 text-teal-600'
  },
  {
    week: '28-32',
    task: 'Finalize Plan',
    description: 'Confirm birth plan with doctor and hospital',
    icon: 'ri-calendar-check-line',
    color: 'bg-rose-100 text-rose-600'
  }
];

export default function PregnancyTracker() {
  const [selectedWeek, setSelectedWeek] = useState<Week>(pregnancyData[0]);
  const [showTimeline, setShowTimeline] = useState(false);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: tabsRef, isVisible: tabsVisible } = useScrollAnimation();
  const { ref: contentRef, isVisible: contentVisible } = useScrollAnimation();

  return (
    <section id="pregnancy-tracker" className="py-20 bg-gradient-to-br from-pink-50 to-purple-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          ref={headerRef}
          className={`text-center mb-12 ${headerVisible ? 'animate-fade-in-down' : 'opacity-0'}`}
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-pink-100 rounded-full mb-4">
            <i className="ri-heart-pulse-line text-pink-600"></i>
            <span className="text-sm font-semibold text-pink-700">Pregnancy Journey</span>
          </div>
          <h2 className="text-5xl font-bold text-gray-900 mb-4">Your Week-by-Week Guide</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Track your pregnancy journey with personalized insights, test schedules, and hospital selection timeline
          </p>
        </div>

        {/* Toggle Buttons */}
        <div
          ref={tabsRef}
          className={`flex justify-center mb-8 ${tabsVisible ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}
        >
          <button
            onClick={() => setShowTimeline(false)}
            className={`px-8 py-4 rounded-full font-semibold text-base transition-all whitespace-nowrap ${
              !showTimeline
                ? 'bg-pink-600 text-white shadow-lg shadow-pink-600/30'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
            }`}
          >
            <i className="ri-calendar-line mr-2"></i>
            Weekly Guide
          </button>
          <button
            onClick={() => setShowTimeline(true)}
            className={`px-8 py-4 rounded-full font-semibold text-base transition-all whitespace-nowrap ${
              showTimeline
                ? 'bg-purple-600 text-white shadow-lg shadow-purple-600/30'
                : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
            }`}
          >
            <i className="ri-hospital-line mr-2"></i>
            Hospital Selection Timeline
          </button>
        </div>

        <div
          ref={contentRef}
          className={contentVisible ? 'animate-fade-in-up animation-delay-300' : 'opacity-0'}
        >
          {!showTimeline ? (
            <>
              {/* Week Selector */}
              <div className="mb-12">
                <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Select Your Week</h3>
                <div className="flex flex-wrap gap-3 justify-center">
                  {pregnancyData.map((week) => (
                    <button
                      key={week.week}
                      onClick={() => setSelectedWeek(week)}
                      className={`px-6 py-3 rounded-full font-semibold text-sm transition-all whitespace-nowrap ${
                        selectedWeek.week === week.week
                          ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white shadow-lg'
                          : 'bg-white text-gray-700 hover:bg-gray-50 border-2 border-gray-200'
                      }`}
                    >
                      Week {week.week}
                    </button>
                  ))}
                </div>
              </div>

              {/* Week Details */}
              <div className="grid lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Baby Development */}
                  <div className="bg-white rounded-3xl shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-pink-100 rounded-xl flex items-center justify-center">
                        <i className="ri-heart-line text-2xl text-pink-600"></i>
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">Week {selectedWeek.week}</h3>
                        <p className="text-base text-gray-600">{selectedWeek.title}</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-pink-50 to-purple-50 rounded-xl">
                        <div className="text-sm font-semibold text-gray-700 mb-2">Baby Size</div>
                        <div className="text-2xl font-bold text-pink-600">{selectedWeek.babySize}</div>
                        <div className="text-sm text-gray-600 mt-1">Weight: {selectedWeek.babyWeight}</div>
                      </div>

                      <div className="p-4 bg-blue-50 rounded-xl">
                        <div className="text-sm font-semibold text-gray-700 mb-2">Key Milestone</div>
                        <div className="text-base text-gray-900">{selectedWeek.milestone}</div>
                      </div>
                    </div>
                  </div>

                  {/* Symptoms */}
                  <div className="bg-white rounded-3xl shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                        <i className="ri-pulse-line text-2xl text-amber-600"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Common Symptoms</h3>
                    </div>
                    <ul className="space-y-3">
                      {selectedWeek.symptoms.map((symptom, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-checkbox-circle-fill text-amber-500 text-xl mt-0.5"></i>
                          <span className="text-base text-gray-700">{symptom}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Tips */}
                  <div className="bg-white rounded-3xl shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                        <i className="ri-lightbulb-line text-2xl text-emerald-600"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Tips for This Week</h3>
                    </div>
                    <ul className="space-y-3">
                      {selectedWeek.tips.map((tip, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-star-fill text-emerald-500 text-xl mt-0.5"></i>
                          <span className="text-base text-gray-700">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Tests */}
                  <div className="bg-white rounded-3xl shadow-2xl p-8">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <i className="ri-test-tube-line text-2xl text-blue-600"></i>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900">Recommended Tests</h3>
                    </div>
                    <ul className="space-y-3">
                      {selectedWeek.tests.map((test, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <i className="ri-flask-line text-blue-500 text-xl mt-0.5"></i>
                          <span className="text-base text-gray-700">{test}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Hospital Selection Timeline */}
              <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-3xl shadow-2xl p-8 mb-8">
                  <h3 className="text-3xl font-bold text-gray-900 mb-4 text-center">
                    Hospital Selection Timeline
                  </h3>
                  <p className="text-lg text-gray-600 text-center mb-8">
                    Follow this timeline to choose the perfect hospital for your delivery
                  </p>

                  <div className="space-y-6">
                    {hospitalSelectionTimeline.map((item, idx) => (
                      <div key={idx} className="relative">
                        {idx !== hospitalSelectionTimeline.length - 1 && (
                          <div className="absolute left-6 top-16 w-0.5 h-full bg-gradient-to-b from-purple-300 to-pink-300"></div>
                        )}
                        <div className="flex gap-6">
                          <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.color} flex-shrink-0 z-10`}>
                            <i className={`${item.icon} text-2xl`}></i>
                          </div>
                          <div className="flex-1 pb-8">
                            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border-2 border-purple-100">
                              <div className="flex items-center justify-between mb-3">
                                <h4 className="text-xl font-bold text-gray-900">{item.task}</h4>
                                <span className="px-3 py-1 bg-white rounded-full text-sm font-semibold text-purple-600">
                                  Week {item.week}
                                </span>
                              </div>
                              <p className="text-base text-gray-700">{item.description}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick Tips */}
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-time-line text-2xl text-blue-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Start Early</h4>
                    <p className="text-sm text-gray-600">Begin research by week 8-12 for best options</p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-hospital-line text-2xl text-purple-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Visit Facilities</h4>
                    <p className="text-sm text-gray-600">Tour at least 3-5 hospitals before deciding</p>
                  </div>
                  <div className="bg-white rounded-2xl p-6 shadow-lg text-center">
                    <div className="w-14 h-14 bg-pink-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <i className="ri-checkbox-circle-line text-2xl text-pink-600"></i>
                    </div>
                    <h4 className="text-lg font-bold text-gray-900 mb-2">Finalize Early</h4>
                    <p className="text-sm text-gray-600">Complete registration by week 24-28</p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-pink-600 to-purple-600 rounded-3xl p-8 text-white">
            <h3 className="text-3xl font-bold mb-4">Need Help Choosing a Hospital?</h3>
            <p className="text-lg mb-6 opacity-90">
              Use our hospital comparison tool to find the perfect facility for your delivery
            </p>
            <button
              onClick={() => document.getElementById('hospitals')?.scrollIntoView({ behavior: 'smooth' })}
              className="px-8 py-4 bg-white text-purple-600 rounded-full font-semibold text-base hover:bg-gray-100 transition-all shadow-lg whitespace-nowrap inline-flex items-center gap-2"
            >
              <span>Explore Hospitals</span>
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
